// Central content sourced from the US Desalination company overview deck.

export const COMPANY = {
  name: "US Desalination",
  tagline: "Delivering Water Security for Communities to Thrive",
  address: {
    building: "JPMorgan Chase Tower",
    line1: "600 Travis Street, Suite 6400",
    city: "Houston, Texas 77002",
  },
  email: "coms@usdesal.com",
};

export const HERO = {
  overline: "SWRO & Brackish Water Infrastructure",
  line1: "Delivering Water",
  line2: "Security",
  line3: "for Communities to Thrive",
  sub: "A full-lifecycle water infrastructure company — originating, developing, financing, and operating seawater (SWRO) and brackish (BWRO) facilities across the United States.",
};

export const MANIFESTO = [
  {
    num: "I",
    title: "Developer + Operator",
    lead: "We own, operate, and optimize.",
    body: "US Desalination originates, develops, finances, and operates SWRO and brackish facilities near high-volume water-user corridors. We retain long-term O&M positions to compound value well beyond project-level equity — building a recurring, high-margin platform, not a one-time build.",
  },
  {
    num: "II",
    title: "Water Supply, Delivered as a Service",
    lead: "Contracted supply. Durable revenue.",
    body: "Our model is anchored on delivering water supply as a service — contracted off-take and O&M revenue layered atop project economics. We contract supply to municipal, regional, and industrial customers, driving costs down through private development discipline.",
  },
  {
    num: "III",
    title: "Proven at Scale",
    lead: "Among the largest SWRO experience in the U.S.",
    body: "Our team has direct, hands-on experience designing, permitting, building, and operating some of the largest seawater desalination facilities in the United States — and has secured first-of-their-kind, large-scale SWRO discharge permits.",
  },
  {
    num: "IV",
    title: "Founded by Industry Veterans",
    lead: "100+ years of heavy-industry experience.",
    body: "Founded in 2023 by veterans of industrial infrastructure and regulatory sectors, our seasoned, nimble team navigates dynamic regulatory environments and transacts with the largest players across the water value chain.",
  },
];

export const STATS = [
  { value: "-40%", label: "Projected long-term gap between water demand and supply across the arid U.S. South and West.", tone: "red" },
  { value: "Declining", label: "Groundwater aquifers and surface reservoirs are being drawn down faster than they can recharge.", tone: "red" },
  { value: "17B+", label: "Gallons per day of new water supply needed across California, Texas, and Florida alone.", tone: "teal" },
  { value: "Drought-Proof Water Supply", label: "Seawater (SWRO) and brackish (BWRO) reverse osmosis is the only long-term, climate-resilient water supply that scales with demand — independent of rainfall, snowpack, or shrinking river allocations.", tone: "feature" },
  { value: "Fastest-Growing", label: "The U.S. South and West lead the nation in population and industrial growth.", tone: "teal" },
  { value: "Surging Demand", label: "Industrial and AI data-center demand is accelerating pressure on already-scarce regional water.", tone: "navy" },
];

export const RGV = {
  overline: "Flagship Project",
  name: "Rio Grande Valley",
  partner: "RGV-Desal, LLC — a joint venture with IDE Technologies",
  description:
    "In partnership with global desalination leader IDE Technologies, US Desalination formed RGV-Desal, LLC to design, develop, finance, and operate a privately funded marine desalination facility — delivering a new, drought-proof water supply to one of the fastest-growing and most water-stressed regions in the country.",
  facts: [
    { label: "Design Capacity", value: "50 MGD" },
    { label: "Technology", value: "Marine Seawater RO" },
    { label: "Water Source", value: "Gulf of Mexico" },
    { label: "Off-Take", value: "Public Utilities & Industrial" },
  ],
  articleSlug: "usd-ide-form-rgv-desal-joint-venture",
};

export const KEY_FUNCTIONS = [
  { title: "Site Selection & Feasibility", desc: "Identifying and securing high-quality sites with the water source, power, and demand fundamentals to support economically advantaged facilities." },
  { title: "Permitting & Entitlements", desc: "Navigating environmental, coastal, and regulatory approvals to de-risk development and clear the path to construction." },
  { title: "Commercialization", desc: "Structuring water-purchase and off-take agreements with utilities and industrial customers to underpin project revenue." },
  { title: "Construction", desc: "Applying deep construction-management acumen to lead EPC delivery — driving schedule, budget, safety and quality control from groundbreaking through commissioning and start-up." },
  { title: "Structured Capital Financing", desc: "Arranging project capital and equity partnerships to reach a disciplined final investment decision (FID)." },
  { title: "Owners Advisor", desc: "Acting as owner's advisor to the equity shareholders — overseeing plant operations and maintenance contractors and managing the commercial relationships that protect long-term asset performance and value." },
];

export const LIFECYCLE = [
  { phase: "Origination & Site Control" },
  { phase: "Permitting & Design" },
  { phase: "Financing" },
  { phase: "Construction & Commissioning" },
  { phase: "Long-Term O&M" },
  { phase: "Asset Optimization" },
];

export const TECHNOLOGIES = [
  {
    tag: "SWRO",
    name: "Seawater Desalination",
    lead: "Large-scale supply for the coast.",
    desc: "Seawater reverse osmosis draws on the ocean — an effectively infinite resource — to solve virtually any water challenge at scale. With no discernible environmental impact and zero aquifer-depletion risk, this proven osmotic technology is deployed widely across the world.",
    facts: [
      { label: "Typical Scale", value: "50–200+ MGD" },
      { label: "Best Suited For", value: "Coastal & near-coast regions" },
      { label: "Value Profile", value: "Infinite source · no aquifer depletion" },
    ],
  },
  {
    tag: "BWRO",
    name: "Brackish Desalination",
    lead: "Distributed supply for inland communities.",
    desc: "Brackish reverse osmosis costs roughly 40% less and permits about 60% faster than large-scale seawater RO — making it ideal for smaller communities and inland regions that need reliable, drought-resilient water quickly.",
    facts: [
      { label: "Typical Scale", value: "5–10 MGD" },
      { label: "Best Suited For", value: "Inland regions & smaller communities" },
      { label: "Value Profile", value: "~40% lower cost · ~60% faster to permit" },
    ],
  },
];

export const LEADERSHIP = [
  { name: "Sean Strawbridge", role: "Chairman of the Board", img: "/assets/person-b.jpg" },
  { name: "Douglas Allison", role: "Board Director", img: "/assets/person-d.jpg" },
  { name: "Earnest Wotring", role: "Board Director", img: "/assets/person-c.jpg" },
  { name: "Debra Baker", role: "Board Director", img: "/assets/person-e.jpg" },
  { name: "Jim Crabtree", role: "Executive Vice President", img: "/assets/person-a.jpg" },
];

export const NAV_LINKS = [
  { label: "Platform", href: "/#platform" },
  { label: "Opportunity", href: "/#opportunity" },
  { label: "Projects", href: "/#projects" },
  { label: "Leadership", href: "/#leadership" },
  { label: "News", href: "/news" },
];
